import * as Device from 'expo-device';
import * as Notifications from 'expo-notifications';
import Constants from 'expo-constants';
import AsyncStorage from '@react-native-async-storage/async-storage';
import * as Sentry from '@sentry/react-native';
import { createDevice } from '@/data/settings';
import ErrorHandler from '@/utils/ErrorHandler';

async function registerForPushNotificationsAsync(isWeb, isAndroid) {
    let token = isWeb ? "DESKTOP" : undefined;

    if (isAndroid) {
        Notifications.setNotificationChannelAsync('default', {
            name: 'default',
            importance: Notifications.AndroidImportance.MAX,
            vibrationPattern: [0, 250, 250, 250],
            lightColor: '#FF231F7C',
        });
    }

    if (Device.isDevice && !isWeb) {
        const { status: existingStatus } = await Notifications.getPermissionsAsync();
        let finalStatus = existingStatus;

        if (existingStatus !== 'granted') {
            try {
                const { status } = await Notifications.requestPermissionsAsync();
                finalStatus = status;
            } catch {
                // requestPermissionsAsync rejects on platforms that don't support
                // interactive notification authorization (notably Mac Catalyst —
                // ERR_PERMISSIONS_REQUEST_NOTIFICATIONS). Not a transient outage,
                // not actionable — skip push entirely on this device.
                // See CHURCH-APP-19V.
                return;
            }
        }

        if (finalStatus !== 'granted') {
            return;
        }

        token = await Notifications.getExpoPushTokenAsync({
            projectId: Constants.expoConfig.extra.eas.projectId,
        });

        return token.data;
    }
}

// Registers this device in the Devices table and stores the new row id as
// DEVICE_ID. Never throws and never stores a bogus id — returns null (after
// surfacing the failure) when registration didn't produce a device row.
export async function registerDevice(isWeb, isAndroid, userID) {
    try {
        let token;

        try {
            token = await registerForPushNotificationsAsync(isWeb, isAndroid);
        } catch (e) {
            // Reaching here means getExpoPushTokenAsync itself failed — Expo's
            // token service has transient outages (503 upstream errors).
            // Permission-request rejections are caught inside
            // registerForPushNotificationsAsync (CHURCH-APP-19V) and never get
            // here. Don't crash login — still register the device with the
            // SIMULATOR fallback so the app-open device guard doesn't log the
            // user out. See CHURCH-APP-13A.
            console.warn('Push token registration failed, continuing without token:', e?.message);
            Sentry.captureMessage('Expo push token fetch failed', { level: 'warning', extra: { message: e?.message } });
        }

        const newDevice = await createDevice({
            friendlyName: Device.deviceName || Device.modelName || (Device.deviceType == 3 ? 'Computer' : 'Unknown Device'),
            deviceType: Device.deviceType,
            modelName: Device.modelName,
            osVersion: Device.osVersion,
            expoPushToken: token || 'SIMULATOR',
            userID
        });

        if (!newDevice?.id) {
            // createDevice already surfaced the failure via ErrorHandler —
            // just don't store "null"/"undefined" as DEVICE_ID.
            return null;
        }

        await AsyncStorage.setItem('DEVICE_ID', newDevice.id);

        return newDevice;
    } catch (e) {
        try {
            ErrorHandler(e instanceof Error ? e : Error('Device registration failed'), { mutation: 'registerDevice', input: { userID } }, 'Something went wrong registering this device');
        } catch {
            // ErrorHandler rethrows network errors for the query system — nothing to retry here
        }
        return null;
    }
}
